import type { PlayerNetState } from '@mn/shared';
import { PLAYER_SKIN_KEYS } from '../assets/asset-keys.js';

// ── Renders OTHER players in the world scene as moving sprites. ──
// Each remote player is drawn from the same MPWSP overworld skin sheets as the local player
// (32px, 4col x 4row: rows DOWN / LEFT / RIGHT / UP). A skin is picked deterministically from
// the sessionId so remote players read as distinct characters. Positions are tweened toward the
// server-authoritative target for smooth movement.

// Standing frame (first column) of each direction row in a skin sheet (protocol lowercase dirs).
const DIR_FRAME: Record<PlayerNetState['dir'], number> = {
  down: 0,
  left: 4,
  right: 8,
  up: 12,
};

/** Deterministically pick a skin asset key from a session id so each remote reads distinct. */
function skinForSession(sessionId: string): string {
  let h = 0;
  for (let i = 0; i < sessionId.length; i++) {
    h = (h * 31 + sessionId.charCodeAt(i)) | 0;
  }
  return PLAYER_SKIN_KEYS[Math.abs(h) % PLAYER_SKIN_KEYS.length];
}

interface RemoteSprite {
  sprite: Phaser.GameObjects.Sprite;
  label: Phaser.GameObjects.Text;
  tween?: Phaser.Tweens.Tween;
}

/** A remote player's session id + display name + live sprite (for anchoring UI / effects). */
export interface RemoteRef {
  sessionId: string;
  name: string;
  sprite: Phaser.GameObjects.Sprite;
}

/** Manages the lifecycle of remote-player sprites + name labels within a single scene. */
export class RemotePlayers {
  #scene: Phaser.Scene;
  #bySession = new Map<string, RemoteSprite>();
  #depth: number;
  /** Optional: invoked when a remote sprite is tapped (touch/click targeting). */
  #onTap?: (ref: RemoteRef) => void;
  /** Optional: snap a target world position onto the walkable map (keeps remotes/bots off walls). */
  #snapFn?: (x: number, y: number) => { x: number; y: number };

  constructor(scene: Phaser.Scene, depth = 1, snapFn?: (x: number, y: number) => { x: number; y: number }) {
    this.#scene = scene;
    this.#depth = depth;
    this.#snapFn = snapFn;
  }

  /** Walkable-snap a target position (no-op when no snap fn is wired). */
  #snap(x: number, y: number): { x: number; y: number } {
    return this.#snapFn ? this.#snapFn(x, y) : { x, y };
  }

  /** Register a tap/click handler; every remote sprite (current + future) becomes interactive. */
  onTap(handler: (ref: RemoteRef) => void): void {
    this.#onTap = handler;
    for (const [sessionId, entry] of this.#bySession) this.#wireTap(sessionId, entry);
  }

  /** The live sprite + name for a session, or null if that player is gone. */
  ref(sessionId: string): RemoteRef | null {
    const entry = this.#bySession.get(sessionId);
    if (!entry || !entry.sprite.active) return null;
    return { sessionId, name: entry.label.text, sprite: entry.sprite };
  }

  has(sessionId: string): boolean {
    return this.#bySession.has(sessionId);
  }

  /** World positions of every active remote sprite — feeds the minimap radar. */
  positions(): { x: number; y: number }[] {
    const out: { x: number; y: number }[] = [];
    for (const entry of this.#bySession.values()) {
      if (entry.sprite.active) out.push({ x: entry.sprite.x, y: entry.sprite.y });
    }
    return out;
  }

  /** Find a visible remote player by display name (case-insensitive) — used to challenge a friend. */
  findByName(name: string): RemoteRef | null {
    const target = name.trim().toLowerCase();
    if (!target) return null;
    for (const [sessionId, entry] of this.#bySession) {
      if (entry.sprite.active && entry.label.text.trim().toLowerCase() === target) {
        return { sessionId, name: entry.label.text, sprite: entry.sprite };
      }
    }
    return null;
  }

  /**
   * Find the nearest remote player to a world position within `maxDist` pixels.
   * Used to pick a duel / trade target. Returns null when nobody is close enough.
   */
  nearest(x: number, y: number, maxDist: number): { sessionId: string; name: string; dist: number } | null {
    let best: { sessionId: string; name: string; dist: number } | null = null;
    for (const [sessionId, entry] of this.#bySession) {
      const dx = entry.sprite.x - x;
      const dy = entry.sprite.y - y;
      const dist = Math.hypot(dx, dy);
      if (dist <= maxDist && (!best || dist < best.dist)) {
        best = { sessionId, name: entry.label.text, dist };
      }
    }
    return best;
  }

  upsert(player: PlayerNetState): void {
    const existing = this.#bySession.get(player.sessionId);
    if (!existing) {
      this.#create(player);
      return;
    }
    this.#move(existing, player);
  }

  remove(sessionId: string): void {
    const entry = this.#bySession.get(sessionId);
    if (!entry) return;
    entry.tween?.stop();
    entry.sprite.destroy();
    entry.label.destroy();
    this.#bySession.delete(sessionId);
  }

  clear(): void {
    for (const id of [...this.#bySession.keys()]) this.remove(id);
  }

  #create(player: PlayerNetState): void {
    const frame = DIR_FRAME[player.dir] ?? 0;
    const { x, y } = this.#snap(player.x, player.y);
    const sprite = this.#scene.add
      .sprite(x, y, skinForSession(player.sessionId), frame)
      .setOrigin(0, 0.2)
      .setScale(2) // skin sheets are 32px; match the local player's overworld scale
      .setDepth(this.#depth)
      .setAlpha(0.95);
    const label = this.#scene.add
      .text(x, y - 16, player.username, {
        fontSize: '16px',
        color: '#ffffff',
        backgroundColor: '#00000080',
        padding: { x: 4, y: 2 },
      })
      .setOrigin(0.5, 1)
      .setDepth(3000); // name floats above the foreground (the sprite stays below it, behind trees)
    const entry: RemoteSprite = { sprite, label };
    this.#bySession.set(player.sessionId, entry);
    if (this.#onTap) this.#wireTap(player.sessionId, entry);
  }

  /** Make one remote sprite tappable; the click stops propagation so it never also walks the player. */
  #wireTap(sessionId: string, entry: RemoteSprite): void {
    if (entry.sprite.input) return; // already interactive
    entry.sprite.setInteractive({ useHandCursor: true });
    entry.sprite.on(
      Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN,
      (_pointer: Phaser.Input.Pointer, _x: number, _y: number, event?: { stopPropagation?: () => void }) => {
        event?.stopPropagation?.();
        const ref = this.ref(sessionId);
        if (ref) this.#onTap?.(ref);
      },
    );
  }

  #move(entry: RemoteSprite, player: PlayerNetState): void {
    const frame = DIR_FRAME[player.dir] ?? 0;
    entry.sprite.setFrame(frame);
    entry.tween?.stop();
    // Remote players move on a grid, one CARDINAL tile at a time. We only ever tween a clean
    // single-axis step — that can never cross a wall corner. We SNAP (no tween) when the delta is:
    //   • a teleport (portal / heal / area change), or
    //   • DIAGONAL — both axes moved at once, which means two perpendicular steps got batched into one
    //     update (an L-turn around a wall). Tweening that straight line cut diagonally THROUGH the wall
    //     corner, which is exactly the reported "players flying / walking through walls" bug.
    // Snap the target onto the walkable map first — keeps remotes/bots off walls/water/trees.
    const { x: tx, y: ty } = this.#snap(player.x, player.y);
    const dx = Math.abs(tx - entry.sprite.x);
    const dy = Math.abs(ty - entry.sprite.y);
    const dist = Math.hypot(dx, dy);
    // Only TWEEN a clean single-axis step of ~one tile; SNAP everything else (a diagonal = batched
    // L-turn whose straight tween cuts through a wall corner, or a multi-tile jump = teleport). Tighter
    // than before (was 96px / 8px) since players still reported sprites "flying" across the map.
    const diagonal = dx > 6 && dy > 6;
    if (dist > 80 || diagonal) {
      entry.sprite.setPosition(tx, ty);
      entry.label.setPosition(tx, ty - 16);
      return;
    }
    entry.tween = this.#scene.add.tween({
      targets: [entry.sprite],
      x: tx,
      y: ty,
      duration: 200,
      onUpdate: () => {
        entry.label.setPosition(entry.sprite.x, entry.sprite.y - 16);
      },
    });
  }
}
